// Pure progression engine. No React, no I/O, no time-of-day side effects.
// The only date source is the explicit `today` argument, so tests stay deterministic.

import type {
  Exercise,
  ExerciseProgress,
  IsoDate,
  ProgressionStandard,
  WorkoutLog,
} from "../types/calisthenics";
import type { LogEntryRow, WorkoutLogRow } from "../services/db";

export type ProgressionStatus = "none" | "beginner" | "intermediate" | "mastery";

/** ADR-0013 D2 — set requirement per tier, constant across the Big 6.
 *  Holds are single efforts, so the per-tier count is always 1 there.
 */
export const TIER_SETS: Record<Exclude<ProgressionStatus, "none">, number> = {
  beginner: 1,
  intermediate: 2,
  mastery: 3,
};

/** The tier a single completed workout reaches. Pure: works on the workout's
 *  sets in isolation. For rep-based exercises the set requirement is per
 *  tier (TIER_SETS); for hold-based skills a single hold at or above the
 *  tier's seconds is enough (the book counts one hold, not three).
 *
 *  Examples with reps: [10] on 10/25/50 → beginner. [25, 25] → intermediate.
 *  [50, 50, 50] → mastery. [50, 10, 10] → beginner (one mastery set is
 *  not mastery — see ADR-0013 D2). */
export function tierOfWorkout(
  sets: Array<{ reps?: number; hold_seconds?: number }>,
  standard: ProgressionStandard,
): ProgressionStatus {
  const isHold = (standard.holdSeconds ?? 0) > 0;
  if (isHold) {
    const bestHold = sets.reduce(
      (m, s) => Math.max(m, s.hold_seconds ?? 0),
      0,
    );
    if (bestHold >= (standard.holdSeconds ?? Infinity)) return "mastery";
    if (bestHold >= (standard.intermediateHoldSeconds ?? Infinity)) return "intermediate";
    if (bestHold >= (standard.beginnerHoldSeconds ?? Infinity)) return "beginner";
    return "none";
  }
  const repsArr = sets.map((s) => s.reps ?? 0);
  const meetMastery = repsArr.filter((r) => r >= standard.masteryReps).length;
  if (meetMastery >= TIER_SETS.mastery) return "mastery";
  const meetIntermediate = repsArr.filter((r) => r >= standard.intermediateReps).length;
  if (meetIntermediate >= TIER_SETS.intermediate) return "intermediate";
  const meetBeginner = repsArr.filter((r) => r >= standard.beginnerReps).length;
  if (meetBeginner >= TIER_SETS.beginner) return "beginner";
  return "none";
}

/**
 * The next step in a chain unlocks only when the immediately preceding step
 * is at mastery. Per ADR-0013 D2 the engine checks `bestTier` (over
 * completed workouts only), not a single best rep.
 */
export function canUnlockNext(
  progress: Record<string, ExerciseProgress>,
  _exercise: Exercise,
  prev: Exercise,
): boolean {
  const prevProgress = progress[prev.id];
  if (!prevProgress) return false;
  return prevProgress.bestTier === "mastery";
}

/** The set of log_entries that belong to a single workout. Pulled out so
 *  the caller can pre-group by `workout` once if it has many entries. */
function entriesByWorkout(
  workouts: WorkoutLogRow[],
  entries: LogEntryRow[],
): Map<string, { workout: WorkoutLogRow; sets: LogEntryRow[] }> {
  const byId = new Map<string, WorkoutLogRow>();
  for (const w of workouts) byId.set(w.id, w);
  const out = new Map<string, { workout: WorkoutLogRow; sets: LogEntryRow[] }>();
  for (const w of workouts) {
    out.set(w.id, { workout: w, sets: [] });
  }
  for (const e of entries) {
    const bucket = out.get(e.workout);
    if (bucket) bucket.sets.push(e);
  }
  return out;
}

/** Pure derivation from local rows. ADR-0013 D2: tiers come from COMPLETED
 *  workouts only; unfinished (completed_at:null) and tombstoned
 *  (deletedAt) workouts contribute nothing. `bestReps` /
 *  `bestHoldSeconds` are informational maxima over completed workouts.
 *  Caller can override the result with a synced `user_progress` row.
 */
export function aggregateProgress(
  workouts: WorkoutLogRow[],
  entries: LogEntryRow[],
): Record<string, ExerciseProgress> {
  const empty: ExerciseProgress = {
    exerciseId: "",
    bestTier: "none",
    bestReps: 0,
    bestHoldSeconds: 0,
    completedWorkouts: 0,
  };
  const out = new Map<string, ExerciseProgress>();

  const completed = workouts.filter(
    (w) => w.completed_at != null && !w.deletedAt,
  );
  const byWorkout = entriesByWorkout(completed, entries);

  for (const [, { sets }] of byWorkout) {
    // One workout → +1 per unique exercise touched.
    const exercisesInWorkout = new Set(sets.map((s) => s.exercise));
    for (const e of sets) {
      const cur =
        out.get(e.exercise) ??
        ({ ...empty, exerciseId: e.exercise } as ExerciseProgress);
      if ((e.reps ?? 0) > (cur.bestReps ?? 0)) cur.bestReps = e.reps ?? 0;
      if ((e.hold_seconds ?? 0) > (cur.bestHoldSeconds ?? 0))
        cur.bestHoldSeconds = e.hold_seconds ?? 0;
      out.set(e.exercise, cur);
    }
    for (const exId of exercisesInWorkout) {
      const cur = out.get(exId);
      if (cur) cur.completedWorkouts = (cur.completedWorkouts ?? 0) + 1;
    }
  }

  // Compute the per-exercise tier over each completed workout's sets.
  // We need the standard for the exercise — derive from `entries.exercise`
  // metadata is not present, so we look it up via the caller's known
  // exercises. To keep the function pure and testable without a registry,
  // the caller passes the standard map in via `withStandards`. This
  // indirection is intentional: the engine never depends on the data
  // loader. Use `aggregateProgressWithStandards` for the typical path.
  // The Map above carries maxima; the caller adds tier on top via
  // `tierOfWorkout`.
  void empty;
  const result: Record<string, ExerciseProgress> = {};
  for (const [id, p] of out.entries()) result[id] = p;
  return result;
}

/** The common path: aggregateProgress + tierOfWorkout, fed with a map of
 *  standards keyed by exercise id. */
export function aggregateProgressWithStandards(
  workouts: WorkoutLogRow[],
  entries: LogEntryRow[],
  standards: Record<string, ProgressionStandard>,
): Record<string, ExerciseProgress> {
  const base = aggregateProgress(workouts, entries);
  const byWorkout = entriesByWorkout(
    workouts.filter((w) => w.completed_at != null && !w.deletedAt),
    entries,
  );
  for (const [, { sets }] of byWorkout) {
    if (sets.length === 0) continue;
    const standard = standards[sets[0]!.exercise];
    if (!standard) continue;
    const tier = tierOfWorkout(
      sets.map((s) => ({ reps: s.reps, hold_seconds: s.hold_seconds })),
      standard,
    );
    const exId = sets[0]!.exercise;
    const cur = base[exId] ?? {
      exerciseId: exId,
      bestTier: "none",
      bestReps: 0,
      bestHoldSeconds: 0,
      completedWorkouts: 0,
    };
    if (tierRank(tier) > tierRank(cur.bestTier)) cur.bestTier = tier;
    base[exId] = cur;
  }
  return base;
}

function tierRank(t: ProgressionStatus): number {
  switch (t) {
    case "mastery":
      return 3;
    case "intermediate":
      return 2;
    case "beginner":
      return 1;
    case "none":
    default:
      return 0;
  }
}

/**
 * Consecutive days ending today (or yesterday — we still call it an active
 * streak so a once-a-day lifter doesn't see a broken streak on rest day).
 * Returns 0 if the most recent workout is older than yesterday.
 */
export function computeStreak(workoutDates: IsoDate[], today: IsoDate): number {
  if (workoutDates.length === 0) return 0;

  const unique = Array.from(new Set(workoutDates)).sort(); // ascending
  const latest = unique[unique.length - 1];
  const dayMs = 86_400_000;
  const latestMs = Date.parse(latest);
  const todayMs = Date.parse(today);
  if (Number.isNaN(latestMs) || Number.isNaN(todayMs)) return 0;
  const gap = Math.round((todayMs - latestMs) / dayMs);
  if (gap > 1) return 0; // missed a day → streak is over

  let streak = 1;
  for (let i = unique.length - 2; i >= 0; i--) {
    const cur = Date.parse(unique[i + 1]);
    const prev = Date.parse(unique[i]);
    const diff = Math.round((cur - prev) / dayMs);
    if (diff === 1) streak += 1;
    else if (diff === 0) continue; // duplicate date, already counted
    else break;
  }
  return streak;
}

/**
 * 0–100 recovery score. A heavy recent training load lowers the score; the
 * UI will warn the user to deload once it falls below 40. The score considers
 * the last seven days only.
 */
export function computeRecoveryScore(
  recentWorkouts: WorkoutLog[],
  today: IsoDate
): number {
  const dayMs = 86_400_000;
  const todayMs = Date.parse(today);
  if (Number.isNaN(todayMs)) return 0;

  const window = 7;
  let load = 0;
  for (const w of recentWorkouts) {
    const t = Date.parse(w.date);
    if (Number.isNaN(t)) continue;
    const daysAgo = Math.round((todayMs - t) / dayMs);
    if (daysAgo < 0 || daysAgo > window) continue;
    // Recency weight: today = 1.0, six days ago = 0.3
    const recency = 1 - 0.7 * (daysAgo / window);
    // Heuristic load: reps weighted lightly, time-under-tension weighted heavier.
    load += recency * (w.totalReps / 8 + w.totalSeconds / 90);
  }
  // 300 effective load units already saturate a deload signal.
  const score = 100 - Math.min(100, (load / 300) * 100);
  return Math.max(0, Math.min(100, Math.round(score)));
}